import { useEffect, useState } from 'react';
import { apiClient, type AllProjectsRow, type MeResponse, ApiError } from '@/lib/api/client';
import { RadialOrbital, type OrbitalItem } from './RadialOrbital';
import { cn } from '@/lib/utils';

type ViewMode = 'orbital' | 'table';
const ALL_VIEW_PREF_KEY = 'apetech-pm:all-projects-view';

/** Stable color hash from workspace id → OKLCH hue (constant lightness/chroma).
 * Used to tint orbital node ring per home workspace so shared projects from
 * different teams are visually grouped without color-only encoding. */
function wsRingColor(ws_id: string): string {
  let h = 0;
  for (let i = 0; i < ws_id.length; i++) h = (h * 31 + ws_id.charCodeAt(i)) >>> 0;
  const hue = h % 360;
  return `oklch(0.7 0.14 ${hue})`;
}

interface Props {
  me: MeResponse;
  onBack: () => void;
  /** Opens the full Project page (tasks tab, etc.), NOT the settings drawer. */
  onOpenProject: (project_id: string) => void;
  onPinChanged: () => void;
}

/**
 * Cross-workspace "All Projects" — flattens every project the user can access
 * via project_members, with home-workspace + owner labels + per-user pin info.
 */
export function AllProjectsView({ me, onBack, onOpenProject, onPinChanged }: Props) {
  const [rows, setRows] = useState<AllProjectsRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [view, setView] = useState<ViewMode>(() => {
    try {
      const v = localStorage.getItem(ALL_VIEW_PREF_KEY);
      return v === 'table' ? 'table' : 'orbital';
    } catch {
      return 'orbital';
    }
  });
  function setViewPersist(v: ViewMode) {
    setView(v);
    try {
      localStorage.setItem(ALL_VIEW_PREF_KEY, v);
    } catch {/* ignore */}
  }
  const SHOW_CLOSED_KEY = 'apetech-pm:all-projects-show-closed';
  const [showClosed, setShowClosed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(SHOW_CLOSED_KEY) === '1';
    } catch {
      return false;
    }
  });
  function setShowClosedPersist(v: boolean) {
    setShowClosed(v);
    try {
      localStorage.setItem(SHOW_CLOSED_KEY, v ? '1' : '0');
    } catch {/* ignore */}
  }

  async function refresh() {
    try {
      const r = await apiClient.allProjects();
      setRows(r.projects);
      setErr(null);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Failed to load');
    }
  }

  useEffect(() => {
    refresh();
  }, []);

  return (
    <main className="min-h-dvh bg-background text-foreground antialiased">
      <header className="border-b border-border">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-3">
          <div className="flex items-center gap-3">
            <button
              onClick={onBack}
              className="rounded-md border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground hover:border-ring/60 hover:text-foreground"
            >
              ← Back
            </button>
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              All projects
            </div>
          </div>
        </div>
      </header>
      <div className="mx-auto max-w-6xl px-6 py-10">
        <div className="flex items-baseline justify-between gap-4">
          <div className="space-y-1">
            <h1 className="text-balance text-3xl font-semibold tracking-tight md:text-4xl">
              All projects
            </h1>
            <p className="font-mono text-xs text-muted-foreground">
              Every project you can access, across all workspaces.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {rows && rows.some((r) => !!r.closed_at) && (
              <button
                type="button"
                onClick={() => setShowClosedPersist(!showClosed)}
                aria-pressed={showClosed}
                className={cn(
                  'rounded-full border px-3 py-1 font-mono text-[10px] uppercase tracking-widest transition-colors',
                  showClosed
                    ? 'border-primary bg-primary/15 text-primary'
                    : 'border-border text-muted-foreground hover:border-ring/60 hover:text-foreground',
                )}
              >
                {(() => {
                  const n = rows.filter((r) => !!r.closed_at).length;
                  return showClosed ? `Hide closed (${n})` : `Show closed (${n})`;
                })()}
              </button>
            )}
            <div
              className="inline-flex items-center gap-px rounded-full border border-border bg-card p-0.5"
              role="group"
              aria-label="View mode"
            >
              {(['orbital', 'table'] as const).map((v) => (
                <button
                  key={v}
                  onClick={() => setViewPersist(v)}
                  aria-pressed={view === v}
                  className={cn(
                    'rounded-full px-3 py-1 font-mono text-[10px] uppercase tracking-widest transition-colors duration-150',
                    view === v
                      ? 'bg-secondary text-secondary-foreground'
                      : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  {v}
                </button>
              ))}
            </div>
          </div>
        </div>
        {err && (
          <div role="alert" className="mt-6 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm">
            {err}
          </div>
        )}
        <div className="mt-8">
          {rows === null ? (
            <div
              className="grid gap-3 md:grid-cols-2 lg:grid-cols-3"
              aria-busy="true"
              aria-label="Loading"
            >
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="h-16 animate-pulse rounded-xl bg-card" />
              ))}
            </div>
          ) : view === 'orbital' ? (
            <RadialOrbital
              center={{
                kind: 'all',
                userInitial: (me.user.email[0] ?? '?').toUpperCase(),
              }}
              items={(showClosed ? rows : rows.filter((r) => !r.closed_at)).map((r) => toOrbitalItem(r, me.user.user_id))}
              onOpenProject={onOpenProject}
              emptyCta={
                <div className="text-xs text-muted-foreground">
                  Create one in any workspace, or accept an invite to a project.
                </div>
              }
            />
          ) : rows.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border bg-card/40 px-5 py-10 text-center">
              <div className="text-sm text-foreground">No projects yet</div>
              <div className="mt-1 text-xs text-muted-foreground">
                Create one in any workspace, or accept an invite to a project.
              </div>
            </div>
          ) : (
            <div className="overflow-hidden rounded-xl border border-border">
              <table className="w-full text-sm">
                <thead className="border-b border-border bg-card/50">
                  <tr className="text-left font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                    <th className="px-4 py-3 font-medium">Project</th>
                    <th className="px-4 py-3 font-medium">Home workspace</th>
                    <th className="px-4 py-3 font-medium">Owner</th>
                    <th className="px-4 py-3 font-medium">Your role</th>
                    <th className="px-4 py-3 font-medium">Pin</th>
                  </tr>
                </thead>
                <tbody>
                  {(showClosed ? rows : rows.filter((r) => !r.closed_at)).map((r) => (
                    <Row
                      key={r.project_id}
                      row={r}
                      myUserId={me.user.user_id}
                      myWorkspaces={me.workspaces}
                      onOpen={() => onOpenProject(r.project_id)}
                      onPinChanged={async () => {
                        await refresh();
                        onPinChanged();
                      }}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </main>
  );
}

function toOrbitalItem(r: AllProjectsRow, myUserId: string): OrbitalItem {
  const isMine = r.home_ws_owner_user_id === myUserId;
  return {
    id: r.project_id,
    name: r.name,
    slug: r.slug,
    role: r.my_role,
    color: r.color,
    image_key: r.image_key,
    emoji: r.emoji,
    task_open: r.task_open ?? 0,
    task_done: r.task_done ?? 0,
    task_total: r.task_total ?? 0,
    members_preview: r.members_preview ?? [],
    member_count: r.member_count ?? 0,
    created_at: r.created_at,
    ring_color: wsRingColor(r.home_ws_id),
    home_ws_name: r.home_ws_name,
    home_ws_owner_email: r.home_ws_owner_email,
    is_mine: isMine,
  };
}

function Row({
  row,
  myUserId,
  myWorkspaces,
  onOpen,
  onPinChanged,
}: {
  row: AllProjectsRow;
  myUserId: string;
  myWorkspaces: MeResponse['workspaces'];
  onOpen: () => void;
  onPinChanged: () => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const isMine = row.home_ws_owner_user_id === myUserId;

  async function pinTo(ws_id: string) {
    setBusy(true);
    try {
      await apiClient.pinProject(row.project_id, ws_id);
      await onPinChanged();
    } finally {
      setBusy(false);
    }
  }
  async function unpin() {
    setBusy(true);
    try {
      await apiClient.unpinProject(row.project_id);
      await onPinChanged();
    } finally {
      setBusy(false);
    }
  }

  return (
    <tr className="border-t border-border first:border-t-0 hover:bg-card/40">
      <td className="px-4 py-3">
        <button onClick={onOpen} className="text-left hover:text-primary">
          <div className="font-medium text-foreground">{row.name}</div>
          <div className="font-mono text-[10px] text-muted-foreground">/p/{row.slug}</div>
        </button>
      </td>
      <td className="px-4 py-3">
        <div className="text-foreground">{row.home_ws_name}</div>
        {!isMine && (
          <div className="font-mono text-[10px] text-muted-foreground">shared from</div>
        )}
      </td>
      <td className="px-4 py-3 text-muted-foreground">
        {isMine ? 'You' : row.home_ws_owner_email}
      </td>
      <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{row.my_role}</td>
      <td className="px-4 py-3">
        {isMine ? (
          <span className="font-mono text-[10px] text-muted-foreground">— home —</span>
        ) : (
          <div className="flex items-center gap-2">
            <label htmlFor={`pin-${row.project_id}`} className="sr-only">
              Pinned workspace for {row.name}
            </label>
            <select
              id={`pin-${row.project_id}`}
              value={row.pinned_ws_id ?? ''}
              onChange={(e) => {
                if (e.target.value) pinTo(e.target.value);
                else unpin();
              }}
              disabled={busy}
              className="rounded-md border border-border bg-background px-2 py-1 text-xs"
            >
              {/* Option labels read as STATE ("Not pinned" / workspace name)
                  not actions ("Pin to..."). Per recognition-over-recall. */}
              <option value="">Not pinned</option>
              {myWorkspaces.map((w) => (
                <option key={w.ws_id} value={w.ws_id}>
                  {w.name}
                </option>
              ))}
            </select>
          </div>
        )}
      </td>
    </tr>
  );
}
